import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { SidebarGroup, SidebarGroupContent, SidebarSeparator } from "@/components/ui/sidebar";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem,
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { 
  CheckCircle2, 
  Clock, 
  MoreVertical, 
  Repeat2, 
  UserCheck, 
  HammerIcon,
  MessageSquare,
  Eye
} from "lucide-react";
import { Link } from "react-router";
import { cn } from "@/lib/utils";
import {format, isPast, isSameDay, startOfDay} from "date-fns";
import { appointment_status } from "@/models/appointment.model";
import { AppointmentChatService } from "@/services/appointment-chat.service";
import { useAppointmentCalendar } from "@/hooks/useAppointmentCalendar";

interface CalendarAppointmentsProps {
  className?: string;
}

export function CalendarAppointments({ className }: CalendarAppointmentsProps) {
  const {
    selectedDate,
    setSelectedDate,
    appointments,
    loading,
    error,
    hasAppointments,
    getAppointmentCount
  } = useAppointmentCalendar({ maxAppointments: 100 });

  // Get status icon
  const getStatusIcon = (status: appointment_status) => {
    switch (status) {
      case appointment_status.pending:
        return <Clock size={14} className="text-yellow-500 dark:text-yellow-400" />;
      case appointment_status.assigned:
        return <UserCheck size={14} className="text-blue-500 dark:text-blue-400" />;
      case appointment_status.inprogress:
        return <HammerIcon size={14} className="text-indigo-500 dark:text-indigo-400" />;
      case appointment_status.done:
        return <CheckCircle2 size={14} className="text-green-500 dark:text-green-400" />;
      case appointment_status.goback:
        return <Repeat2 size={14} className="text-red-500 dark:text-red-400" />;
      default:
        return <Clock size={14} className="text-gray-500" />;
    }
  };

  // Get status color
  const getStatusColor = (status: appointment_status) => {
    switch (status) {
      case appointment_status.pending:
        return "text-yellow-500 dark:text-yellow-400";
      case appointment_status.assigned:
        return "text-blue-500 dark:text-blue-400";
      case appointment_status.inprogress:
        return "text-indigo-500 dark:text-indigo-400";
      case appointment_status.done:
        return "text-green-500 dark:text-green-400";
      case appointment_status.goback:
        return "text-red-500 dark:text-red-400";
      default:
        return "text-gray-400";
    }
  };

  // Handle opening chat
  const handleOpenChat = async (appointmentId: string) => {
    try {
      const chatRoom = await AppointmentChatService.getAppointmentGroupChat(appointmentId);
      if (chatRoom) {
        window.location.href = `/messages/${chatRoom.id}`;
      } else {
        console.warn('No chat room found for appointment:', appointmentId);
      }
    } catch (error) {
      console.error('Failed to open chat:', error);
    }
  };

  return (
    <div className={cn("w-full", className)}>
      {/* Calendar */}
      <SidebarGroup className="px-0">
        <SidebarGroupContent>
          <Calendar 
            mode="single"
            selected={selectedDate}
            onSelect={(date) => date && setSelectedDate(date)}
            className="[&_[role=gridcell].bg-accent]:bg-sidebar-primary [&_[role=gridcell].bg-accent]:text-sidebar-primary-foreground [&_[role=gridcell]]:w-[33px] [&_.rdp-day_selected]:bg-sidebar-primary [&_.rdp-day_selected]:text-sidebar-primary-foreground [&_.rdp-day_selected]:font-bold [&_.rdp-day_selected]:border-2 [&_.rdp-day_selected]:border-sidebar-primary"
            modifiers={{
              hasAppointments: (date) => hasAppointments(date) && !isSameDay(date, selectedDate),
              pastDate: (date) => isPast(startOfDay(date)) && !isSameDay(date, new Date())
            }}
            modifiersStyles={{
              hasAppointments: {
                backgroundColor: 'hsl(var(--primary))',
                color: 'hsl(var(--primary-foreground))',
                fontWeight: 'bold',
                borderRadius: '6px'
              },
              pastDate: {
                opacity: 0.5,
                color: 'hsl(var(--muted-foreground))'
              }
            }}
          />
        </SidebarGroupContent>
      </SidebarGroup>

      <SidebarSeparator className="mx-0" />

      {/* Appointments for selected date */}
      <div className="p-4 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium">
            {format(selectedDate, 'MMM d, yyyy')}
          </h3>
          <Badge variant="outline" className="text-xs">
            {appointments.length} {appointments.length === 1 ? 'appointment' : 'appointments'}
          </Badge>
        </div>

        {loading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-16 bg-muted animate-pulse rounded-lg" />
            ))}
          </div>
        ) : error ? (
          <div className="text-sm text-destructive text-center py-4">
            {error}
          </div>
        ) : appointments.length === 0 ? (
          <div className="text-sm text-muted-foreground text-center py-8">
            No appointments scheduled for this day
          </div>
        ) : (
          <div className="space-y-3">
            {appointments.map((appointment) => (
              <Card
                key={appointment.id}
                className={cn(
                  "border-l-[3px] border-l-current bg-white dark:bg-background overflow-hidden transition-all p-0",
                  getStatusColor(appointment.status)
                )}
              >
                <CardContent className="p-0">
                  <div className="flex justify-between items-start p-3">
                    <div className="flex flex-col items-start gap-1 flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Tooltip>
                          <TooltipTrigger>{getStatusIcon(appointment.status)}</TooltipTrigger>
                          <TooltipContent>
                            <p className="capitalize">{appointment.status.replace('_', ' ')}</p>
                          </TooltipContent>
                        </Tooltip>
                        <h4 className="font-semibold text-sm text-foreground truncate">
                          {appointment.property.name}
                        </h4>
                      </div>
                      <p className="text-xs text-muted-foreground truncate">
                        {appointment.property.unit_number}
                      </p>
                      <p className="text-xs font-medium text-muted-foreground truncate">
                        {appointment.service.name}
                      </p>
                      {appointment.assigned_user?.name && (
                        <p className="text-xs text-muted-foreground truncate">
                          Assigned: {appointment.assigned_user.name}
                        </p>
                      )}
                    </div>
                    
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-6 w-6 p-0">
                          <MoreVertical className="h-3 w-3" />
                          <span className="sr-only">Open menu</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem asChild>
                          <Link to={`/appointments/${appointment.id}`} className="flex items-center">
                            <Eye className="mr-2 h-4 w-4" />
                            View Details
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleOpenChat(appointment.id)}
                          className="flex items-center"
                        >
                          <MessageSquare className="mr-2 h-4 w-4" />
                          Open Chat
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}